import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {Avatar as PrimeAvatar} from "primereact/avatar";
import {BreadCrumb} from "primereact/breadcrumb";
import {Button} from "primereact/button";
import {DataView} from "primereact/dataview";
import {Divider} from "primereact/divider";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {ProgressSpinner} from "primereact/progressspinner";
import Avatar from "../../system/components/avatar";
import AvatarGroup from "../../system/components/avatar-group";
import Can from "../../system/can";
import Icon from "../../system/components/icon";
import {setMessage} from "../../system/app-slice";
import {colorToHexAlpha} from "../../system/utils";
import {useTranslate} from "../../system/useTranslate";
import {usePermission} from "../../system/usePermission";
import {useTeam} from "../../models/team/use-team";
import {usePublishAiScope} from "../../system/ai-scope-context";
import DatatableEmptyMessage from "../../system/components/datatable-empty-message";
import PageHeaderButton from "../components/page-header-button";
import CreateTeamDialog from "./component/create-team-dialog";
import {teamDisplayName} from "./team-display-name";

export {teamDisplayName};

function Index(props) {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const permission = usePermission();
    const teamApi = useTeam();
    const membership = useSelector((state) => state.app.workspaceMembershipData);
    const [teams, setTeams] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [createVisible, setCreateVisible] = useState(false);

    const load = async () => {
        if (!membership?.workspace?.id) return;
        setLoading(true);
        setError(null);
        try { setTeams(await teamApi.getTeams(membership.workspace.id)); }
        catch (requestError) { setError(requestError?.message || "team.list.load-error"); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [membership?.workspace?.documentId, membership?.workspace?.id]);

    const filtered = useMemo(() => {
        const query = search.trim().toLocaleLowerCase();
        return query ? teams.filter((item) => [item.name, teamDisplayName(item, translate)]
            .some((name) => String(name || "").toLocaleLowerCase().includes(query))) : teams;
    }, [search, teams, translate]);

    usePublishAiScope({activeTab: "teams-list", selectedEntityIds: teams.map((item) => item.documentId || item.id), filters: {scopeSelectionProvided: true}});

    const create = async ({value}) => {
        if (!permission.can("manage-teams", true)) return;
        try {
            await teamApi.createTeam({...value, workspace: membership.workspace.documentId || membership.workspace.id});
            dispatch(setMessage({severity: "success", content: translate("team.create.success")}));
            setCreateVisible(false);
            await load();
        } catch (requestError) {
            dispatch(setMessage({severity: "error", content: translate(requestError?.message || "team.create.error")}));
            throw requestError;
        }
    };

    const itemTemplate = (team) => <div className="p-3 lg:w-4 md:w-6 w-full text-xs">
        <article
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/teams/${team.documentId || team.id}`)}
            onKeyDown={(event) => ["Enter", " "].includes(event.key) && navigate(`/teams/${team.documentId || team.id}`)}
            className="surface-card h-full w-full shadow-1 border-1 border-100 border-round-2xl cursor-pointer hover:surface-50"
        >
            <div
                className="w-full p-4 flex flex-column align-items-center justify-content-center gap-4 border-round-2xl"
                style={{background: `linear-gradient(225deg, ${colorToHexAlpha(team?.iconColor?.color, 0.2)} 0%, ${colorToHexAlpha(team?.iconColor?.color, 0.2)} 50%, transparent 50%) top right / 40px 40px no-repeat`}}
            >
                <header className="flex justify-content-between align-items-center w-full">
                    <div className="flex align-items-center gap-1 min-w-0">
                        <PrimeAvatar shape="circle" style={{backgroundColor: colorToHexAlpha(team?.iconColor?.color, 0.1), color: colorToHexAlpha(team?.iconColor?.color, 1)}} icon={<Icon icon={team?.iconColor?.icon || "mi mi:groups"} className="text-lg"/>}/>
                        <strong className="text-lg text-900 min-w-0 text-overflow-ellipsis overflow-hidden white-space-nowrap">{teamDisplayName(team, translate)}</strong>
                    </div>
                    <Icon icon="mi mi:more_vert" className="text-xl text-600"/>
                </header>
                <div className="surface-100 flex align-items-center w-full border-round-xl gap-2 p-2">
                    <div className="flex flex-column justify-content-center align-items-center flex-1 gap-2 font-bold">
                        <span className="text-400 font-normal text-xs">{translate("projects")}</span>
                        <span className="text-red-600 text-sm">{Number(team.projects?.length || 0).toLocaleString("fa-IR")} {translate("project")}</span>
                    </div>
                    <Divider layout="vertical" className="m-0"/>
                    <div className="flex flex-column justify-content-center align-items-center flex-1 gap-2 font-bold">
                        <span className="text-400 font-normal text-xs">{translate("goals")}</span>
                        <span className="text-red-600 text-sm">{Number(team.goals?.length || 0).toLocaleString("fa-IR")} {translate("goal")}</span>
                    </div>
                </div>
                <Divider className="my-0"/>
                <footer className="flex align-items-center justify-content-between gap-3 w-full">
                    <AvatarGroup>{(team.members || []).map((item) => <Avatar key={item.documentId || item.id} memberData={item.member}/>)}</AvatarGroup>
                    <div className="flex flex-column gap-1 min-w-0">
                        <span className="font-bold">{translate("team-owner")}</span>
                        <strong className={`text-xs ${team?.owner?.member ? "text-900" : "text-500 font-normal"}`}>
                            {team?.owner?.member?.fullname || translate("team.owner.unassigned")}
                        </strong>
                    </div>
                </footer>
            </div>
        </article>
    </div>;

    return <div className="h-full flex flex-column overflow-hidden surface-50">
        <div className="border-bottom-1 border-200 flex flex-column pb-2">
            <BreadCrumb separatorIcon={<Icon icon="mi mi:chevron_left text-xs"/>} className="bg-transparent border-none text-xs" model={[{label: translate("teams")}]} />
            <PageHeaderButton title={props.name || translate("teams")} end={<Can permission="manage-teams"><Button className="text-xs gap-2" icon="pi pi-plus" label={translate("create-team")} onClick={() => setCreateVisible(true)}/></Can>}/>
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-3 flex flex-column gap-3">
            <IconField className="w-full md:w-24rem"><InputIcon className="pi pi-search"/><InputText className="w-full" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={translate("search-teams-placeholder")}/></IconField>
            {loading ? <div className="flex-1 flex align-items-center justify-content-center"><ProgressSpinner/></div>
                : error ? <div className="flex-1 flex flex-column align-items-center justify-content-center gap-3"><Message severity="error" text={translate(error)}/><Button outlined className="text-xs gap-2" icon="pi pi-refresh" label={translate("team.retry")} onClick={load}/></div>
                : <DataView
                    value={filtered}
                    paginator
                    rows={9}
                    layout="grid"
                    itemTemplate={itemTemplate}
                    emptyMessage={<DatatableEmptyMessage/>}
                />}
        </div>
        <CreateTeamDialog visible={createVisible} setVisible={setCreateVisible} onSubmit={create}/>
    </div>;
}

export default Index;
